import i18n from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { defaultsDeep, first } from 'lodash';
import { ReactNode, useEffect } from 'react';
import { I18nextProvider as LibI18nextProvider, initReactI18next } from 'react-i18next';
import { z } from 'zod';
import { zodI18nMap } from 'zod-i18n-map';
import { useAuthContext } from '../AuthProvider';
import { ClientId, useConfig } from '@nexelem/config';

declare module 'i18next' {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

export type LANGUAGES_KEYS = 'pl' | 'en' | 'en-GB' | 'de';

const modules: Record<ClientId, Record<LANGUAGES_KEYS, () => Promise<unknown>>> = {
  [ClientId.defaultClient]: import.meta.glob('../../translations/generic/*/index.ts', { import: 'default' }),
  [ClientId.espro]: import.meta.glob('../../translations/espro/*/index.ts', { import: 'default' }),
  [ClientId.etyflex]: import.meta.glob('../../translations/etyflex/*/index.ts', { import: 'default' }),
  [ClientId.siap]: import.meta.glob('../../translations/siap/*/index.ts', { import: 'default' }),
  [ClientId.sanok]: import.meta.glob('../../translations/sanok/*/index.ts', { import: 'default' }),
  [ClientId.ekoplast]: import.meta.glob('../../translations/ekoplast/*/index.ts', { import: 'default' }),
  [ClientId.nanochem]: import.meta.glob('../../translations/nanochem/*/index.ts', { import: 'default' }),
};

const emptyTranslation = () => Promise.resolve({});

const getClientResources = async (clientId: ClientId, language: string) => {
  const extractLanguage = (path: string) => first(path.replace(`../../translations/${clientId}/`, '').split('/'));
  const [_, module] =
    Object.entries(modules[clientId] ?? {}).find(([name]) => extractLanguage(name) === language) ?? [];
  return module ?? emptyTranslation;
};

const defaultResources: Record<string, () => Promise<{ default: unknown }>> = {
  en: () => import('../../translations/generic/en'),
  'en-GB': () => import('../../translations/generic/en'),
  pl: () => import('../../translations/generic/pl'),
  de: () => import('../../translations/generic/de'),
};

const zodResources: Record<string, () => Promise<{ default: unknown }>> = {
  en: () => import('zod-i18n-map/locales/en/zod.json'),
  'en-GB': () => import('zod-i18n-map/locales/en/zod.json'),
  pl: () => import('zod-i18n-map/locales/pl/zod.json'),
  de: () => import('zod-i18n-map/locales/de/zod.json'),
};

type I18nextProviderProps = {
  readonly children: ReactNode;
};
export const I18nextProvider = ({ children }: I18nextProviderProps) => {
  const { user } = useAuthContext();
  const { clientId } = useConfig();
  useEffect(() => {
    i18n
      .use(initReactI18next)
      .use(
        resourcesToBackend(async (language: string, namespace: string) => {
          if (namespace === 'zod') {
            const zodResource = zodResources[language];
            if (!zodResource) {
              throw new Error(`Zod translations not found for language: ${language}`);
            }
            return await zodResource();
          }

          const defaultTranslations = defaultResources[language];
          if (!defaultTranslations) {
            throw new Error(`Default translations not found for language: ${language}`);
          }
          const clientResources = await getClientResources(clientId, language);
          const [defaultLoaded, clientLoaded] = await Promise.all([defaultTranslations(), clientResources()]);
          return defaultsDeep(clientLoaded, defaultLoaded.default);
        }),
      )
      .init({
        returnNull: false,
        partialBundledLanguages: true,
        lng: navigator.language,
        ns: [clientId, 'zod', 'generic'],
        fallbackLng: 'en',
        fallbackNS: ClientId.defaultClient,
        debug: false,
        interpolation: {
          escapeValue: false,
        },
        react: {
          useSuspense: true,
          bindI18n: 'languageChanged loaded',
          bindI18nStore: 'added removed',
          nsMode: 'default',
        },
      });
    i18n.on('languageChanged', (lng) => (document.documentElement.lang = lng));
  }, [clientId]);

  useEffect(() => {
    if (user?.language) {
      i18n.changeLanguage(user.language);
    }
  }, [user?.language]);

  return <LibI18nextProvider i18n={i18n}>{children}</LibI18nextProvider>;
};

// Add globaly translation to zod errors.
z.setErrorMap(zodI18nMap);
